import React, { useCallback, useEffect, useState } from 'react';
import { Modal, Input, Select, Tag, Button, Spin, Empty } from 'antd';
import { toast } from 'react-toastify';
import axios from 'axios';
import config from '../../config';
import usePermissions from '../../hooks/usePermissions';
import './acomptePanel.css';

/*
  Panneau de gestion des acomptes d'UNE réservation (ou option / pré-commande).

  Rappel des règles (côté serveur, reflétées ici) :
   · versement facultatif, DOLLARS ENTIERS ;
   · une réservation gardée par un acompte vivant ne peut plus être libérée sans
     régler le sort de l'argent → on affiche pourquoi ;
   · issue AU CAS PAR CAS (avoir / remboursement / retenue), MOTIF obligatoire ;
   · encaisser = guichet (reservations.acompte) ; décider du sort = décision
     financière (reservations.acompte_regler).

  Piège du dépôt respecté : aucun sous-composant défini dans le rendu.
*/

const DOMAIN = config.REACT_APP_SERVER_DOMAIN;
const money = (v) => `${Number(v || 0).toLocaleString('fr-FR')} $`;

const STATUT_TAG = {
  VERSE: <Tag color="processing">versé</Tag>,
  IMPUTE: <Tag color="success">imputé</Tag>,
  AVOIR: <Tag color="gold">avoir</Tag>,
  REMBOURSE: <Tag>remboursé</Tag>,
  RETENU: <Tag color="volcano">retenu</Tag>,
  ANNULE: <Tag>annulé (saisie)</Tag>,
};

// Issues possibles depuis un versement encore VERSE.
const ISSUES = [
  { value: 'AVOIR', label: 'Garder en avoir', aide: 'Crédit au nom de la cliente, réutilisable plus tard.' },
  { value: 'REMBOURSE', label: 'Rembourser', aide: 'Rendre l’argent en espèces.' },
  { value: 'RETENU', label: 'Retenir', aide: 'Conservé par la boutique.' },
];

export default function AcomptePanel({ cible, open, onClose, onChange }) {
  const { can } = usePermissions();
  const peutEncaisser = can('reservations.acompte');
  const peutRegler = can('reservations.acompte_regler');

  const [chargement, setChargement] = useState(true);
  const [etat, setEtat] = useState(null);
  const [busy, setBusy] = useState(false);

  // Formulaire d'ajout
  const [montant, setMontant] = useState('');
  const [moyen, setMoyen] = useState('cash');

  const params = useCallback(() => (
    cible?.type === 'precommande' ? { id_precommande: cible.id } : { id_reservation: cible.id }
  ), [cible]);

  const charger = useCallback(async () => {
    if (!cible) return;
    setChargement(true);
    try {
      const { data } = await axios.get(`${DOMAIN}/api/acomptes`, { params: params() });
      setEtat(data);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Chargement des acomptes impossible.');
      setEtat(null);
    } finally {
      setChargement(false);
    }
  }, [cible, params]);

  useEffect(() => { if (open) { setMontant(''); setMoyen('cash'); charger(); } }, [open, charger]);

  const apres = async (message) => {
    await charger();
    if (onChange) onChange();
    if (message) toast.success(message);
  };

  const verser = async () => {
    const m = Number(montant);
    if (!Number.isInteger(m) || m <= 0) return toast.error('Montant en dollars entiers, supérieur à zéro.');
    setBusy(true);
    try {
      await axios.post(`${DOMAIN}/api/acomptes`, { ...params(), montant: m, moyen });
      setMontant('');
      await apres('Acompte encaissé.');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Encaissement impossible.');
    } finally { setBusy(false); }
  };

  const annulerSaisie = (a) => {
    let motif = '';
    Modal.confirm({
      title: 'Annuler ce versement (erreur de saisie) ?',
      content: (
        <div>
          <p className="ac-modal-aide">{money(a.montant)} · {a.moyen || 'sans moyen'}. Réservé aux erreurs de frappe.</p>
          <Input.TextArea rows={2} maxLength={200} placeholder="Raison" onChange={(e) => { motif = e.target.value; }} />
        </div>
      ),
      okText: 'Annuler le versement', cancelText: 'Fermer', okButtonProps: { danger: true },
      onOk: async () => {
        if (!motif.trim()) { toast.error('La raison est obligatoire.'); throw new Error('motif'); }
        await axios.put(`${DOMAIN}/api/acomptes/${a.id_acompte}/annuler-saisie`, { motif });
        await apres('Versement annulé.');
      },
    });
  };

  const reglerIssue = (a) => {
    let issue = 'AVOIR';
    let motif = '';
    Modal.confirm({
      title: 'Que devient cet acompte ?',
      width: 460,
      content: (
        <div className="ac-issue">
          <p className="ac-modal-aide">{money(a.montant)} versé le {a.date_cr ? String(a.date_cr).slice(0, 10) : ''}.</p>
          <Select
            defaultValue="AVOIR" style={{ width: '100%', marginBottom: 8 }}
            onChange={(v) => { issue = v; }}
            options={ISSUES.map((i) => ({ value: i.value, label: i.label }))}
          />
          <p className="ac-modal-aide">{ISSUES[0].aide}</p>
          <Input.TextArea rows={2} maxLength={200} placeholder="Motif (obligatoire)" onChange={(e) => { motif = e.target.value; }} />
        </div>
      ),
      okText: 'Valider la décision', cancelText: 'Annuler',
      onOk: async () => {
        if (!motif.trim()) { toast.error('Le motif est obligatoire.'); throw new Error('motif'); }
        await axios.put(`${DOMAIN}/api/acomptes/${a.id_acompte}/issue`, { issue, motif });
        await apres('Sort de l’acompte réglé.');
      },
    });
  };

  const acomptes = etat?.acomptes || [];
  const solde = etat?.solde || 0;
  const liberation = etat?.liberation;
  const valeur = etat?.cible?.valeur || 0;
  const cibleLabel = cible?.type === 'precommande' ? 'option' : 'réservation';

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={520}
      title={`Acomptes — ${cibleLabel} #${cible?.id || ''}`}
      destroyOnClose
    >
      {chargement ? (
        <div className="ac-centre"><Spin /></div>
      ) : (
        <div className="acompte-panel">
          <div className="ac-resume">
            <div className="ac-resume-bloc">
              <span className="ac-k">Déjà versé</span>
              <span className="ac-v">{money(solde)}</span>
            </div>
            {valeur > 0 ? (
              <div className="ac-resume-bloc">
                <span className="ac-k">Valeur {cibleLabel}</span>
                <span className="ac-v">{money(valeur)}</span>
              </div>
            ) : null}
            {etat?.jours_immobilisation > 0 ? (
              <div className="ac-resume-bloc">
                <span className="ac-k">Immobilisée depuis</span>
                <span className="ac-v">{etat.jours_immobilisation} j</span>
              </div>
            ) : null}
          </div>

          {liberation && !liberation.autorise ? (
            <div className="ac-blocage">
              {liberation.vivants} versement(s) pour {money(liberation.montant)} en attente :
              réglez leur sort avant de pouvoir libérer cette {cibleLabel}.
            </div>
          ) : null}

          {acomptes.length === 0 ? (
            <Empty description="Aucun acompte." image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            <ul className="ac-liste">
              {acomptes.map((a) => (
                <li key={a.id_acompte} className="ac-ligne">
                  <div className="ac-ligne-texte">
                    <span className="ac-ligne-montant">{money(a.montant)} {STATUT_TAG[a.statut] || a.statut}</span>
                    <span className="ac-ligne-detail">
                      {[a.moyen, a.date_cr ? String(a.date_cr).slice(0, 10) : null, a.encaisse_par].filter(Boolean).join(' · ')}
                    </span>
                    {a.motif ? <span className="ac-ligne-motif">{a.motif}</span> : null}
                  </div>
                  {a.statut === 'VERSE' ? (
                    <div className="ac-ligne-actions">
                      {peutRegler ? (
                        <Button size="small" onClick={() => reglerIssue(a)}>Sort…</Button>
                      ) : null}
                      {peutEncaisser ? (
                        <Button size="small" type="text" danger onClick={() => annulerSaisie(a)}>Annuler saisie</Button>
                      ) : null}
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          )}

          {peutEncaisser && etat?.cible?.statut && ['active', 'EN_ATTENTE'].includes(etat.cible.statut) ? (
            <div className="ac-ajout">
              <span className="ac-ajout-titre">Encaisser un acompte</span>
              <div className="ac-ajout-champs">
                <Input
                  value={montant}
                  onChange={(e) => setMontant(e.target.value.replace(/[^\d]/g, ''))}
                  inputMode="numeric" placeholder="0" suffix="$" className="ac-montant"
                />
                <Select
                  value={moyen} onChange={setMoyen} className="ac-moyen"
                  options={[
                    { value: 'cash', label: 'Cash' },
                    { value: 'mpesa', label: 'M-Pesa' },
                    { value: 'airtel', label: 'Airtel Money' },
                    { value: 'orange', label: 'Orange Money' },
                    { value: 'banque', label: 'Banque' },
                  ]}
                />
                <Button type="primary" loading={busy} onClick={verser} disabled={!montant}>Encaisser</Button>
              </div>
            </div>
          ) : null}
        </div>
      )}
    </Modal>
  );
}
